import {
    Directive,
    Input,
    ElementRef,
    HostListener,
    ViewContainerRef,
    ComponentFactoryResolver,
    ComponentRef,
    SimpleChanges,
    OnInit,
    OnChanges,
    ApplicationRef,
    Injector
} from '@angular/core';
import { TooltipComponent } from '../../components/core/tooltip';
import { OnDestroy } from '@angular/core';

/**
 * Usage example:
 * On hover, display a tool tip above the button
 * <button (click)="doSomething()" tooltip [tooltipDisabled]="true" text="My Text" position="top">Hello World</button>
 */
@Directive({
    selector: '[tooltip]'
})
export class TooltipDirective implements OnInit, OnChanges, OnDestroy {
    @Input() text: string;
    @Input() setTextAsInnerHTML = false;
    @Input() position = 'right'; // accepted values: ['right', 'left', 'top', 'bottom']
    // if set to true, then tooltip will be hidden even when mouse is over the target element
    @Input() tooltipDisabled = false;
    @Input() extraOffset = 0;
    @Input() customClass = '';

    private tooltip: ComponentRef<TooltipComponent>;
    private mouseX: number;
    private mouseY: number;
    private offsetX = 10;
    private offsetY = 5;

    constructor(
        private elem: ElementRef,
        private applicationRef: ApplicationRef,
        private injector: Injector,
        private componentFactory: ComponentFactoryResolver
    ) {}

    @HostListener('mouseenter', ['$event']) mouseEnter(event: MouseEvent) {
        this.mouseX = event.x;
        this.mouseY = event.y;
        if (!this.tooltipDisabled) {
            this.tooltip.instance.show = true;
        }
        this.calculateTooltipPosition();
    }

    @HostListener('mousemove', ['$event']) mouseMove(event: MouseEvent) {
        if (this.tooltip.instance.show && this.position === 'follow') {
            this.mouseX = event.x;
            this.mouseY = event.y;
            this.calculateTooltipPosition();
        }
    }

    @HostListener('mouseleave') mouseLeave() {
        this.tooltip.instance.show = false;
    }

    @HostListener('window:resize') async windowResized() {
        if (this.tooltip && this.tooltip.instance) {
            await this.calculateTooltipPosition();
        }
    }

    @HostListener('mousewheel', ['$event']) async onWindowScroll(event: MouseEvent) {
        if (this.tooltip && this.tooltip.instance) {
            const boundingRect = this.elem.nativeElement.getBoundingClientRect();
            const isMouseOver = this.isMouseOver(boundingRect, event.x, event.y);
            if (!isMouseOver && this.tooltip.instance.show) {
                this.tooltip.instance.show = false;
            } else if (isMouseOver && !this.tooltip.instance.show) {
                this.tooltip.instance.show = true;
            }
        }
    }

    /**
     * On init, resolve the component -- returns an object that
     * knows how to create the component
     * then, create the component from the component factory
     */
    async ngOnInit(): Promise<any> {
        const tooltipFactory = this.componentFactory.resolveComponentFactory(TooltipComponent);
        this.tooltip = tooltipFactory.create(this.injector);
        this.applicationRef.attachView(this.tooltip.hostView);

        const location = this.applicationRef.components[0].location.nativeElement;
        location.appendChild(this.tooltip.location.nativeElement);

        await this.calculateTooltipPosition();

        this.tooltip.instance.customClass = this.customClass;

        // If the element we attached this directive to is disabled, we need to manually hide it,
        // because the events that would normally do this don't trigger for disabled elements.
        this.tooltip.instance.isDisabled = this.elem.nativeElement.disabled;
        const disabledElementObserver = new MutationObserver(mutations => {
            const disabledToggled = mutations.find(mutation => mutation.attributeName === 'disabled');
            if (disabledToggled) {
                this.tooltip.instance.isDisabled = (disabledToggled.target as HTMLInputElement).disabled;
            }
            // If the tooltip's element gets disabled, manually hide the tooltip as well.
            // This prevents the tooltip from appearing if the element's reenabled, but the user isn't hovering over it..
            if (this.tooltip.instance.isDisabled) {
                this.tooltip.instance.show = false;
            }
        });
        disabledElementObserver.observe(this.elem.nativeElement, { attributes: true });
    }

    async ngOnChanges(inputChanges: SimpleChanges): Promise<any> {
        if (!this.tooltip || (inputChanges.text && inputChanges.text.firstChange)) {
            return;
        }
        if (inputChanges.tooltipDisabled && inputChanges.tooltipDisabled.currentValue) {
            this.tooltip.instance.show = false;
        }
        await this.calculateTooltipPosition();
    }

    public ngOnDestroy() {
        if (this.tooltip) {
            this.tooltip.destroy();
        }
    }

    private async calculateTooltipPosition(): Promise<any> {
        this.tooltip.instance.text = this.text;
        this.tooltip.instance.setTextAsInnerHTML = this.setTextAsInnerHTML;
        this.tooltip.instance.position = this.position;
        this.tooltip.instance.horizontalOffset = 0;

        await this.catchRenderingThreadsUp();

        const tooltipOffset = 4 + this.extraOffset;

        // Element tooltip is attached to
        const rect = this.elem.nativeElement.getBoundingClientRect();

        // Tooltip wrapper
        const tooltipRect = this.tooltip.location.nativeElement.children[0].getBoundingClientRect();
        const windowWidth = window.innerWidth || document.documentElement.clientWidth;
        const windowHeight = window.innerHeight || document.documentElement.clientHeight;

        let positionX = 0;
        let positionY = 0;

        switch (this.position) {
            case 'right':
                positionX = rect.x + rect.width + tooltipOffset;
                positionY = rect.y + rect.height / 2 - tooltipRect.height / 2;
                break;
            case 'left':
                positionX = rect.x - tooltipRect.width - tooltipOffset;
                positionY = rect.y + rect.height / 2 - tooltipRect.height / 2;
                break;
            case 'bottom':
                positionX = rect.x + rect.width / 2 - tooltipRect.width / 2;
                positionY = rect.y + rect.height + tooltipOffset;
                break;
            case 'top':
                positionX = rect.x + rect.width / 2 - tooltipRect.width / 2;
                // Remove the 4px default offset to avoid altering tooltips without a defined offset.
                positionY = rect.y - rect.height - tooltipRect.height - (tooltipOffset - 4);
                break;
            case 'follow':
                positionX = this.mouseX + this.offsetX;
                positionY = this.mouseY + this.offsetY;
                break;
            default:
                break;
        }

        this.tooltip.instance.positionX = Math.floor(positionX);
        this.tooltip.instance.positionY = Math.floor(positionY);

        /**
         * See BB-5597
         * We recalculate the bounding client rect after the view is fully rendered so we can ensure the tooltip doesn't extend beyond the edge of the window
         */
        await this.catchRenderingThreadsUp();
        const tooltipRectUpdated = this.tooltip.location.nativeElement.children[0].getBoundingClientRect();

        if (!this.isTooltipInView(tooltipRectUpdated, windowWidth, windowHeight)) {
            switch (true) {
                case tooltipRectUpdated.right >= windowWidth:
                    // tooltip is overflowing the right side of the viewport
                    this.tooltip.instance.horizontalOffset = windowWidth - tooltipRectUpdated.right - 2;
                    break;
                case tooltipRectUpdated.left < 0:
                    // tooltip is overflowing the left side of the viewport
                    this.tooltip.instance.horizontalOffset = 0;
                    break;
            }
        }
    }

    private isTooltipInView(boundingRect: ClientRect, windowWidth: number, windowHeight: number): boolean {
        return (
            boundingRect.top >= 0 &&
            boundingRect.left >= 0 &&
            boundingRect.right <= windowWidth &&
            boundingRect.bottom <= windowHeight
        );
    }

    private isMouseOver(boundingRect, mouseX: number, mouseY: number): boolean {
        return (
            mouseX > boundingRect.x &&
            mouseX < boundingRect.x + boundingRect.width &&
            mouseY > boundingRect.y &&
            mouseY < boundingRect.y + boundingRect.height
        );
    }

    private catchRenderingThreadsUp(): Promise<boolean> {
        return new Promise((resolve, reject) => {
            setTimeout(() => {
                return resolve(true);
            }, 10);
        });
    }
}



// WEBPACK FOOTER //
// ./src/directives/tooltip/index.ts